import { type ErrorEvent as SentryErrorEvent, type init } from '@sentry/react';

import { stripQuery } from '@apps/web/lib/strip_query';

// `@sentry/react` v10 does not re-export the `TransactionEvent` type, so derive
// it from the public `init` options' `beforeSendTransaction` parameter.
export type SentryTransactionEvent = Parameters<
    NonNullable<Parameters<typeof init>[0]['beforeSendTransaction']>
>[0];

// Known-benign browser/extension lifecycle messages. These are environmental
// races -- tab teardown, service-worker eviction, message-port disconnects, or
// a ResizeObserver delivery deferred to the next frame -- not application
// defects. They carry no actionable stack and inflate error rates. Matched as
// substrings because the surrounding prefix/suffix varies.
const BENIGN_LIFECYCLE_ERROR_FRAGMENTS: ReadonlyArray<string> = [
    'The browser is shutting down.',
    'No current window',
    'No window with id',
    'No tab with id',
    'Extension context invalidated',
    'Could not establish connection. Receiving end does not exist.',
    'The message port closed before a response was received.',
    'A listener indicated an asynchronous response by returning true, but the message channel closed',
    'ResizeObserver loop completed with undelivered notifications.',
    'ResizeObserver loop limit exceeded',
];

// Scripts injected into Safari pages (extensions, host apps) that assume a
// WKWebView native bridge throw when `window.webkit` is absent. The app never
// uses that bridge, so these always come from third-party code.
const INJECTED_NATIVE_BRIDGE_ERROR_FRAGMENTS: ReadonlyArray<string> = [
    "evaluating 'window.webkit.messageHandlers",
    "(reading 'messageHandlers')",
];

export function isBenignBrowserErrorMessage(message: string): boolean {
    return [
        ...BENIGN_LIFECYCLE_ERROR_FRAGMENTS,
        ...INJECTED_NATIVE_BRIDGE_ERROR_FRAGMENTS,
    ].some((fragment) => message.includes(fragment));
}

// A dynamic route chunk whose fetch fails or is aborted. Each engine words it
// differently; WebKit's phrasing dominates because iOS Safari surfaces the
// abort that fires when a page load loses connectivity or navigates away.
// The last entry is Vite's own wording: its preload helper rejects the same
// import when the chunk's stylesheet fails to load.
const MODULE_IMPORT_ERROR_FRAGMENTS: ReadonlyArray<string> = [
    'Importing a module script failed.',
    'Failed to fetch dynamically imported module',
    'error loading dynamically imported module',
    'Unable to preload CSS for',
];

const INJECTED_DOM_SERIALIZATION_ERROR_FRAGMENTS: ReadonlyArray<string> = [
    'Converting circular structure to JSON',
    "object with constructor 'HTMLAnchorElement'",
    "property '__reactFiber$",
    "property 'stateNode' closes the circle",
];

// CefSharp's JavaScript binding, injected by Microsoft Outlook / Defender
// Safe Links link-scanning hosts, rejects with this bare string when its host
// object is gone. It carries no stack and never originates from our code.
const CEFSHARP_BINDING_ERROR_PATTERN =
    /^(?:Non-Error promise rejection captured with value: )?Object Not Found Matching Id:\d+, MethodName:\w+, ParamCount:\d+$/;

function isCefSharpBindingErrorMessage(message: string): boolean {
    return CEFSHARP_BINDING_ERROR_PATTERN.test(message);
}

function hasFirstPartyStack(error: unknown, pageOrigin: string): boolean {
    return (
        error instanceof Error &&
        typeof error.stack === 'string' &&
        error.stack
            .split('\n')
            .slice(1)
            .some((stackFrame) => stackFrame.includes(pageOrigin))
    );
}

export function shouldToastUncaughtError(
    event: ErrorEvent | PromiseRejectionEvent,
    pageOrigin: string = window.location.origin,
): boolean {
    if ('reason' in event) {
        if (hasFirstPartyStack(event.reason, pageOrigin)) {
            return true;
        }

        const message =
            event.reason instanceof Error
                ? event.reason.message
                : String(event.reason);
        return (
            !isBenignBrowserErrorMessage(message) &&
            !isCefSharpBindingErrorMessage(message)
        );
    }

    if (
        isBenignBrowserErrorMessage(event.message) ||
        (event.filename.length === 0 &&
            !hasFirstPartyStack(event.error, pageOrigin))
    ) {
        return false;
    }

    if (hasFirstPartyStack(event.error, pageOrigin)) {
        return true;
    }

    try {
        return new URL(event.filename).origin === pageOrigin;
    } catch {
        return false;
    }
}

export function isBenignLifecycleError(event: SentryErrorEvent): boolean {
    const values = event.exception?.values ?? [];
    const primaryExceptionValue =
        values.length > 0 ? values[values.length - 1]?.value : undefined;
    const candidates = [event.message, primaryExceptionValue].filter(
        (text): text is string => typeof text === 'string',
    );
    return candidates.some(isBenignBrowserErrorMessage);
}

function isInjectedCefSharpBindingError(event: SentryErrorEvent): boolean {
    const values = event.exception?.values ?? [];
    const primaryException =
        values.length > 0 ? values[values.length - 1] : undefined;
    const value = primaryException?.value;
    if (typeof value !== 'string' || !isCefSharpBindingErrorMessage(value)) {
        return false;
    }
    const frames = primaryException?.stacktrace?.frames ?? [];
    return !frames.some((frame) => frame.in_app);
}

function isInjectedDomSerializationError(event: SentryErrorEvent): boolean {
    if (event.transaction !== '/sign-in') {
        return false;
    }

    const values = event.exception?.values ?? [];
    const primaryException =
        values.length > 0 ? values[values.length - 1] : undefined;
    const value = primaryException?.value;
    if (
        typeof value !== 'string' ||
        !INJECTED_DOM_SERIALIZATION_ERROR_FRAGMENTS.every((fragment) =>
            value.includes(fragment),
        )
    ) {
        return false;
    }

    // Email-security scanners can inject an appendChild wrapper that tries to
    // serialize React-managed DOM nodes. Require its anonymous frame so a real
    // application serialization bug with the same message still reaches us.
    return (
        primaryException?.stacktrace?.frames?.some(
            (frame) =>
                frame.filename === '<anonymous>' &&
                frame.function?.endsWith('.appendChild'),
        ) ?? false
    );
}

// A failed chunk load rejects the import promise, which TanStack Router turns
// into a rendered error. `index.html` reloads the page on the same failure, so
// that rendered error is never seen -- report it only when the reload was
// throttled and the user was actually left looking at the error boundary.
function isSelfHealingModuleImportError(event: SentryErrorEvent): boolean {
    if (!window.__phoebeChunkReloadPending) {
        return false;
    }

    const values = event.exception?.values ?? [];
    const value = values.length > 0 ? values[values.length - 1]?.value : null;
    return (
        typeof value === 'string' &&
        MODULE_IMPORT_ERROR_FRAGMENTS.some((fragment) =>
            value.includes(fragment),
        )
    );
}

// Vendor widgets that load their own scripts into the page. An error thrown
// from their code is theirs to fix, even when a timer or listener wrapper
// from our vendor bundle (Sentry or Datadog instrumentation) called them.
const THIRD_PARTY_SCRIPT_ORIGINS: ReadonlyArray<string> = [
    'https://js.intercomcdn.com',
];

const FIRST_PARTY_VENDOR_CHUNK_PATH_PREFIX = '/assets/initial-vendor-';

function frameUrl(filename: string | undefined): URL | null {
    if (filename === undefined) {
        return null;
    }
    try {
        return new URL(filename);
    } catch {
        return null;
    }
}

function isFirstPartyAppFrame(filename: string | undefined): boolean {
    const url = frameUrl(filename);
    return (
        url !== null &&
        url.origin === window.location.origin &&
        !url.pathname.startsWith(FIRST_PARTY_VENDOR_CHUNK_PATH_PREFIX)
    );
}

// Drops an error thrown inside a third-party widget script when none of our
// application code is on the stack. An error from a call our code made into
// the widget (e.g. `Intercom('shutdown')`) keeps our frame and still reports.
function isThirdPartyScriptError(event: SentryErrorEvent): boolean {
    const values = event.exception?.values ?? [];
    const frames =
        (values.length > 0
            ? values[values.length - 1]?.stacktrace?.frames
            : undefined) ?? [];
    // Sentry orders frames oldest first, so the last one threw.
    const throwingFrameUrl = frameUrl(frames[frames.length - 1]?.filename);
    return (
        throwingFrameUrl !== null &&
        THIRD_PARTY_SCRIPT_ORIGINS.includes(throwingFrameUrl.origin) &&
        !frames.some((frame) => isFirstPartyAppFrame(frame.filename))
    );
}

export function scrubErrorEventUrls(event: SentryErrorEvent): SentryErrorEvent {
    if (event.transaction) {
        event.transaction = stripQuery(event.transaction);
    }
    if (event.request?.url) {
        event.request.url = stripQuery(event.request.url);
    }
    event.breadcrumbs?.forEach((breadcrumb) => {
        if (breadcrumb.data) {
            for (const key of ['url', 'from', 'to']) {
                const value = breadcrumb.data[key];
                if (typeof value === 'string') {
                    breadcrumb.data[key] = stripQuery(value);
                }
            }
        }
    });
    return event;
}

export function shouldDropSentryError(event: SentryErrorEvent): boolean {
    return (
        isBenignLifecycleError(event) ||
        isInjectedCefSharpBindingError(event) ||
        isInjectedDomSerializationError(event) ||
        isSelfHealingModuleImportError(event) ||
        isThirdPartyScriptError(event)
    );
}

// Span data keys whose values are full or path-relative URLs we can path-strip
// while keeping the endpoint path.
const URL_SPAN_DATA_KEYS = ['http.url', 'url', 'http.target'] as const;

// Sentry browser tracing records the transaction name, the request URL, and a
// URL on each fetch/XHR span. Our API client serializes user-entered `search`
// terms (e.g. a client or caregiver name) into query params, so strip query
// strings from every URL before a transaction leaves the browser. The endpoint
// path alone is enough for per-endpoint latency.
export function scrubTransactionEventUrls(
    event: SentryTransactionEvent,
): SentryTransactionEvent {
    if (event.transaction) {
        event.transaction = stripQuery(event.transaction);
    }
    if (event.request?.url) {
        event.request.url = stripQuery(event.request.url);
    }
    event.spans?.forEach((span) => {
        if (span.description) {
            span.description = stripQuery(span.description);
        }
        for (const key of URL_SPAN_DATA_KEYS) {
            const value = span.data[key];
            if (typeof value === 'string') {
                span.data[key] = stripQuery(value);
            }
        }
        // `http.query` holds only the query string (there is no path to keep),
        // so drop it wholesale rather than path-stripping.
        if (typeof span.data['http.query'] === 'string') {
            span.data['http.query'] = '';
        }
    });
    return event;
}
